import React, { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import Form from '@/common/components/Form/index';
import { SelectMeta, TextInput } from '@/common/components/Inputs/index';
import styles from '@/modules/admin-reseller-form/styles.less';

export const DEFAULT_RESELLER_VALUES = {
  name: '',
  contactPersonFullName: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  country: 'Canada',
  city: '',
  provinceState: '',
  postalCode: '',
  notes: '',
};

const ResellerFormFields = ({
  formId,
  initialValues = DEFAULT_RESELLER_VALUES,
  onSubmit,
  onFormStateChange,
  className,
  borderless = false,
}) => {
  const { t } = useTranslation();

  const validation = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t('Field is required')),
        contactPersonFullName: yup
          .string()
          .trim()
          .required(t('Field is required')),
        email: yup
          .string()
          .trim()
          .email(t('Enter valid email'))
          .required(t('Field is required')),
        phone: yup.string().trim().required(t('Field is required')),
        addressLine1: yup.string().trim().required(t('Field is required')),
        addressLine2: yup.string().trim(),
        country: yup.string().trim().required(t('Field is required')),
        city: yup.string().trim().required(t('Field is required')),
        provinceState: yup.string().trim().required(t('Field is required')),
        postalCode: yup.string().trim(),
        notes: yup.string().trim(),
      }),
    [t]
  );

  const renderInput = useCallback(
    ({ Component = TextInput, className = styles.field, ...props }) => (
      <Component key={props.name} className={className} {...props} />
    ),
    []
  );

  const resolvedInitialValues = useMemo(
    () => ({
      ...DEFAULT_RESELLER_VALUES,
      ...initialValues,
    }),
    [initialValues]
  );

  return (
    <Form
      id={formId}
      className={className}
      validation={validation}
      validationMode="onChange"
      initialValues={resolvedInitialValues}
      onSubmit={onSubmit}
      onFormStateChange={onFormStateChange}
    >
      <div className={cx(styles.card, borderless && styles.cardBorderless)}>
        <div className={styles.block}>
          <div className={styles.blockHeader}>{t('General info')}</div>
          <div className={styles.grid}>
            {renderInput({
              name: 'name',
              label: t('Reseller name'),
              required: true,
            })}
            {renderInput({
              name: 'contactPersonFullName',
              label: t('Contact person full name'),
              required: true,
            })}
            {renderInput({
              name: 'email',
              label: t('Email'),
              required: true,
            })}
            {renderInput({
              name: 'phone',
              label: t('Tel. (mobile)'),
              required: true,
              mask: '000-000-0000',
            })}
          </div>
        </div>

        <div className={styles.block}>
          <div className={styles.blockHeader}>{t('Address')}</div>
          <div className={styles.grid}>
            {renderInput({
              name: 'addressLine1',
              label: t('Address line 1'),
              required: true,
            })}
            {renderInput({
              name: 'addressLine2',
              label: t('Address line 2'),
            })}
          </div>
          <div className={styles.gridLine}>
            {renderInput({
              name: 'country',
              label: t('Country'),
              required: true,
              Component: SelectMeta,
              metaName: 'DD_COUNTRY',
              disablePortal: borderless,
            })}
            {renderInput({
              name: 'city',
              label: t('City'),
              required: true,
            })}
            {renderInput({
              name: 'provinceState',
              label: t('Province / State'),
              required: true,
              Component: SelectMeta,
              metaName: 'DD_PROV_TERR',
              disablePortal: borderless,
            })}
            {renderInput({
              name: 'postalCode',
              label: t('Postal code'),
              mask: 'a0a 0a0',
              placeholder: t('A0A 0A0'),
            })}
          </div>
        </div>

        <div className={styles.block}>
          <div className={styles.blockHeader}>{t('Notes')}</div>
          <div className={styles.gridSingle}>
            {renderInput({
              name: 'notes',
              label: t('Notes'),
              multiline: true,
              minRows: 1,
              maxRows: 3,
              inputProps: { maxLength: 2000 },
            })}
          </div>
        </div>
      </div>
    </Form>
  );
};

export default ResellerFormFields;
